import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Tooltip,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  strapiDeleteSurveyTeam,
  strapiUpdateSurveyTeam,
  strapiGetWorkOrder,
  strapiListSamplingPlansForWorkOrder,
  strapiListSurveyTeamSchedules,
  strapiListSurveyTeamsForWorkOrder,
  parseSurveyTeamScheduledDates,
  surveyTeamRowScheduledOnDate,
  type WorkOrderRomanQuarter,
} from '../../api/strapiCrm';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import type { StrapiManagedUserRow } from '../../api/strapiUsers';
import { useAuth } from '../../auth/AuthContext';
import { canEditWorkOrder } from '../SamplingSchedulesPage/samplingScheduleAccess';
import { appDocumentTitle } from '../../appMeta';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
  useCreateSampleDetailSurveyorMutation,
  useUpdateWorkOrderMutation,
  useUsersQuery,
  useWorkOrderDetailQuery,
} from '../../query/hooks';
import { queryKeys } from '../../query/queryKeys';
import { PAGE_SHELL_SCROLLABLE_TABS_SX, PageShell } from '../../layout/PageShell';
import { CollapsibleKeteranganSplit } from '../../ui/CollapsibleKeteranganSplit';
import { WorkOrderDetailIntroBlurb } from './WorkOrderDetailRoute/TabScreens/WorkOrderMeta';
import {
  WorkOrderSamplingPlanTab,
  type WorkOrderSamplingPlanTabHandle,
} from './WorkOrderDetailRoute';
import { newDraftSurveyorKey, validatePendingSurveyorDraftsForWorkOrderSave, type PendingSampleSurveyorDraft } from './pendingSurveyorDraft';
import { WORK_ORDER_STATUSES, workOrderStatusLabel, WORK_ORDER_TESTING_DATES_HELPER, isoToDatetimeLocal, type WorkOrderStatus } from './workOrderShared';
import { WorkOrderStatusMenuItemLabel, WorkOrderStatusTooltip } from './WorkOrderStatusHover';
import {
  allVisibleSamplesHaveEquipmentChecklist,
  workOrderStatusAfterEquipmentChecklistComplete,
} from './workOrderEquipmentChecklist';
import { quotationUsesSampleGroups } from '../QuotationsPage/quotationWorkflow';
import {
  samplesHaveAirGroup,
  samplingPlanTabLabel,
} from './samplingPlanAirGroup';
import {
  buildWorkOrderRouteHash,
  parseWorkOrderRouteHash,
  resolveSamplingPlanRouteTab,
  type WorkOrderRouteTab,
} from './workOrderRouteHash';
import { romanMonthMenuItems } from './workOrderRomanMonth';

const statuses = WORK_ORDER_STATUSES;

type SurveyTeamDraft = {
  draftKey: string;
  documentId: string | null;
  user: StrapiManagedUserRow | null;
  is_lead: boolean;
};

function scheduleDatesForUser(cells: Set<string>, userId: string): string[] {
  return [...cells]
    .filter((key) => key.startsWith(`${userId}:`))
    .map((key) => key.slice(userId.length + 1))
    .sort();
}

function buildScheduleCellsFromSurveyTeams(
  rows: DynamicEntityRow[],
  userLookup: StrapiManagedUserRow[],
): Set<string> {
  const cells = new Set<string>();
  for (const row of rows) {
    const user = dynamicUserToManagedUser(row.user_idx, userLookup);
    if (!user) continue;
    for (const date of parseSurveyTeamScheduledDates(row.scheduled_date)) {
      cells.add(`${user.id}:${date}`);
    }
  }
  return cells;
}

function assigneeRelationPayload(user: StrapiManagedUserRow): number | { connect: string[] } {
  if (user.documentId) {
    return { connect: [user.documentId] };
  }
  if (user.numericId != null) {
    return user.numericId;
  }
  throw new Error('User tidak punya id yang valid untuk Strapi.');
}

/** Bandingkan user surveyor di response Strapi (tanpa `id`) vs pilihan Autocomplete */
function surveyorAlreadyOnSample(
  serverUser: { username?: string; email?: string } | null | undefined,
  candidate: StrapiManagedUserRow,
): boolean {
  const se = serverUser?.email?.trim().toLowerCase();
  const ce = candidate.email?.trim().toLowerCase();
  if (se && ce) return se === ce;
  const su = serverUser?.username?.trim().toLowerCase();
  const cu = candidate.username?.trim().toLowerCase();
  return Boolean(su && cu && su === cu);
}

function dynamicUserToManagedUser(raw: unknown, options: StrapiManagedUserRow[]): StrapiManagedUserRow | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const documentId = String(row.documentId ?? '').trim();
  const idRaw = row.id;
  const numericId = idRaw != null && Number.isFinite(Number(idRaw)) ? Number(idRaw) : null;
  const hit = options.find(
    (u) => (documentId && u.documentId === documentId) || (numericId != null && u.numericId === numericId),
  );
  if (hit) return hit;
  return {
    id: numericId != null ? String(numericId) : documentId || 'u:unknown',
    documentId: documentId || null,
    numericId,
    username: String(row.username ?? ''),
    email: String(row.email ?? ''),
    blocked: false,
    userTypeCode: null,
    userTypeName: null,
  };
}

function userDisplayName(user: StrapiManagedUserRow | null | undefined): string {
  if (!user) return 'User belum dipilih';
  return [user.username?.trim() || user.email || user.id, user.email ? `(${user.email})` : ''].filter(Boolean).join(' ');
}

function dynamicRelation(raw: DynamicEntityRow, field: string): Record<string, unknown> | null {
  const value = raw[field];
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function dateOnly(raw: string): string {
  return raw.trim() ? raw.slice(0, 10) : '';
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addWeeks(weekStartText: string, weeks: number): string {
  const base = new Date(`${weekStartText}T00:00:00`);
  if (Number.isNaN(base.getTime())) return startOfWeek('');
  return startOfWeek(isoDate(addDays(base, weeks * 7)));
}

const SCHEDULE_WEEKDAY_HEADER_LABELS = ['Ming', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'] as const;

function scheduleDayHeaderLabel(isoDateStr: string): string {
  const d = new Date(`${isoDateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDateStr;
  const weekday = SCHEDULE_WEEKDAY_HEADER_LABELS[d.getDay()] ?? '';
  return `${d.getDate()}-${weekday}`;
}

function scheduleWeekRangeDateLabel(isoDateStr: string): string {
  return isoDateStr.replace(/-/g, '/');
}

function scheduleDayTitle(isoDateStr: string): string {
  const d = new Date(`${isoDateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDateStr;
  return d.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
}

/** Awal minggu (Minggu) selaras dengan kolom Min–Sab di tabel schedule. */
function startOfWeek(dateText: string): string {
  const base = dateText ? new Date(`${dateText}T00:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return isoDate(new Date());
  return isoDate(addDays(base, -base.getDay()));
}

function surveyDateRange(startRaw: string, endRaw: string, fallbackRaw: string): string[] {
  const startText = dateOnly(startRaw) || dateOnly(fallbackRaw);
  if (!startText) return [];
  const endText = dateOnly(endRaw) || startText;
  const start = new Date(`${startText}T00:00:00`);
  const endCandidate = new Date(`${endText}T00:00:00`);
  const end = Number.isNaN(endCandidate.getTime()) || endCandidate < start ? start : endCandidate;
  if (Number.isNaN(start.getTime())) return [];

  const days: string[] = [];
  for (let cursor = start, i = 0; cursor <= end && i < 31; cursor = addDays(cursor, 1), i++) {
    days.push(isoDate(cursor));
  }
  return days;
}

export function WorkOrderEditRoute() {
  const { jwt, user, loading: authLoading } = useAuth();
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { documentId: rawId } = useParams<{ documentId: string }>();
  const documentId = rawId ? decodeURIComponent(rawId) : null;
  const allowEditWorkOrder = canEditWorkOrder(user);

  useEffect(() => {
    if (!documentId || authLoading) return;
    if (allowEditWorkOrder) return;
    navigate(`/sampling/work-order/${encodeURIComponent(documentId)}/view`, { replace: true });
  }, [allowEditWorkOrder, authLoading, documentId, navigate]);

  const detailQ = useWorkOrderDetailQuery(jwt, documentId);
  const usersQ = useUsersQuery(jwt, { emailContains: '', status: 'active' });
  const surveyTeamsQ = useQuery({
    queryKey: ['strapi', 'surveyTeamsByWorkOrder', jwt, documentId],
    queryFn: () => strapiListSurveyTeamsForWorkOrder(jwt!, documentId!),
    enabled: Boolean(jwt && documentId),
  });
  const updateMut = useUpdateWorkOrderMutation(jwt);
  const createSurveyorMut = useCreateSampleDetailSurveyorMutation(jwt, documentId);

  const defaultYear = useMemo(() => new Date().getFullYear(), []);

  const [workOrderDate, setWorkOrderDate] = useState('');
  const [status, setStatus] = useState<WorkOrderStatus>('draft');
  const [romanQuarter, setRomanQuarter] = useState<WorkOrderRomanQuarter>('I');
  const [calendarYear, setCalendarYear] = useState(defaultYear);
  const [testingStart, setTestingStart] = useState('');
  const [testingEnd, setTestingEnd] = useState('');
  const [fieldsReady, setFieldsReady] = useState(false);
  /** Surveyor dari modal tab Sampel — commit ke API saat Simpan work order */
  const [pendingSurveyorDrafts, setPendingSurveyorDrafts] = useState<PendingSampleSurveyorDraft[]>([]);
  const [surveyTeamDrafts, setSurveyTeamDrafts] = useState<SurveyTeamDraft[]>([]);
  const [surveyTeamsReady, setSurveyTeamsReady] = useState(false);
  const [surveyScheduleDialogOpen, setSurveyScheduleDialogOpen] = useState(false);
  const [surveyScheduleWeekStart, setSurveyScheduleWeekStart] = useState('');
  const [savedScheduleCells, setSavedScheduleCells] = useState<Set<string>>(() => new Set());
  const [draftScheduleCells, setDraftScheduleCells] = useState<Set<string>>(() => new Set());
  const [surveyScheduleSaveError, setSurveyScheduleSaveError] = useState<string | null>(null);
  const [saveAllInProgress, setSaveAllInProgress] = useState(false);
  /** Validasi saat tombol «Simpan work order» (tanggal/tahun, dll.) */
  const [workOrderSaveValidationError, setWorkOrderSaveValidationError] = useState<string | null>(null);
  const samplingPlanAirRef = useRef<WorkOrderSamplingPlanTabHandle | null>(null);
  const samplingPlanNonAirRef = useRef<WorkOrderSamplingPlanTabHandle | null>(null);
  /** Duplikat surveyor di tab Sampel (modal draf); tampil segera, bukan bergantung ke simpan WO */
  const [surveyorDraftError, setSurveyorDraftError] = useState<string | null>(null);

  const romanOptions = useMemo(() => romanMonthMenuItems(), []);

  const { tab: activeTab } = useMemo(
    () => parseWorkOrderRouteHash(location.hash),
    [location.hash],
  );

  useDocumentTitle(appDocumentTitle('Ubah work order'));

  const picOptions = useMemo(() => {
    const rows = usersQ.data?.rows ?? [];
    return rows.filter((u) => u.documentId != null || u.numericId != null);
  }, [usersQ.data?.rows]);

  /** Autocomplete tim survey: gabungkan daftar user aktif dengan user yang sudah di-draft. */
  const picAutocompleteOptions = useMemo(() => {
    const extras = surveyTeamDrafts.map((d) => d.user).filter(Boolean) as StrapiManagedUserRow[];
    const merged = [...picOptions];
    for (const user of extras) {
      const inList = merged.some((u) => u.id === user.id || (user.documentId && u.documentId === user.documentId));
      if (!inList) merged.unshift(user);
    }
    return merged;
  }, [picOptions, surveyTeamDrafts]);
  const surveyTeamUsers = useMemo(
    () =>
      surveyTeamDrafts
        .map((row) => row.user)
        .filter((user): user is StrapiManagedUserRow => user != null),
    [surveyTeamDrafts],
  );
  const surveyScheduleDates = useMemo(
    () => surveyDateRange(testingStart, testingEnd, workOrderDate),
    [testingEnd, testingStart, workOrderDate],
  );
  const baseSurveyDate = surveyScheduleDates[0] ?? workOrderDate;
  const activeWeekStart = surveyScheduleWeekStart || startOfWeek(baseSurveyDate);
  const surveyScheduleWeekDates = useMemo(
    () => {
      const start = new Date(`${activeWeekStart}T00:00:00`);
      return Array.from({ length: 7 }, (_, idx) => isoDate(addDays(start, idx)));
    },
    [activeWeekStart],
  );
  const surveyScheduleUserIds = useMemo(
    () =>
      surveyTeamUsers
        .map((user) => user.numericId)
        .filter((id): id is number => id != null && Number.isFinite(id)),
    [surveyTeamUsers],
  );
  const surveySchedulesQ = useQuery({
    queryKey: ['strapi', 'surveyTeamSchedules', jwt, surveyScheduleUserIds.join(',')],
    queryFn: () => strapiListSurveyTeamSchedules(jwt!, surveyScheduleUserIds),
    enabled: Boolean(jwt && surveyScheduleUserIds.length),
  });

  const wo = detailQ.data;

  useEffect(() => {
    if (!wo || !detailQ.isSuccess) return;
    setWorkOrderDate(wo.work_order_date?.slice(0, 10) ?? '');
    setStatus((wo.progress_status as WorkOrderStatus) ?? 'draft');
    setRomanQuarter((wo.roman_quarter as WorkOrderRomanQuarter) ?? 'I');
    setCalendarYear(wo.calendar_year != null ? Number(wo.calendar_year) : defaultYear);
    setTestingStart(isoToDatetimeLocal(wo.testing_start_date));
    setTestingEnd(isoToDatetimeLocal(wo.testing_end_date));
    setFieldsReady(true);
  }, [wo, detailQ.isSuccess, defaultYear]);

  useEffect(() => {
    setSurveyTeamsReady(false);
  }, [documentId]);

  useEffect(() => {
    if (!surveyTeamsQ.isSuccess || surveyTeamsReady) return;
    setSurveyTeamDrafts(
      surveyTeamsQ.data.map((row) => ({
        draftKey: String(row.documentId || `survey-team-${row.id ?? Math.random()}`),
        documentId: String(row.documentId ?? '').trim() || null,
        user: dynamicUserToManagedUser(row.user_idx, picAutocompleteOptions),
        is_lead: Boolean(row.is_lead),
      })),
    );
    setSavedScheduleCells(buildScheduleCellsFromSurveyTeams(surveyTeamsQ.data, picAutocompleteOptions));
    setSurveyTeamsReady(true);
  }, [surveyTeamsQ.isSuccess, surveyTeamsQ.data, surveyTeamsReady, picAutocompleteOptions]);

  const samples = detailQ.data?.sample_informations ?? [];
  const hasAirSamples = samplesHaveAirGroup(samples, 'air');
  const hasNonAirSamples = samplesHaveAirGroup(samples, 'non_air');
  const resolvedSamplingPlanTab = resolveSamplingPlanRouteTab(activeTab, {
    hasAir: hasAirSamples,
    hasNonAir: hasNonAirSamples,
  });

  useEffect(() => {
    if (!detailQ.data) return;
    if (activeTab !== 'sampling_plan_air' && activeTab !== 'sampling_plan_non_air') {
      return;
    }
    const resolved = resolveSamplingPlanRouteTab(activeTab, {
      hasAir: samplesHaveAirGroup(detailQ.data.sample_informations ?? [], 'air'),
      hasNonAir: samplesHaveAirGroup(detailQ.data.sample_informations ?? [], 'non_air'),
    });
    if (resolved && resolved !== activeTab) {
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: buildWorkOrderRouteHash(resolved, null),
        },
        { replace: true },
      );
    }
  }, [activeTab, detailQ.data, location.pathname, location.search, navigate]);

  useEffect(() => {
    setPendingSurveyorDrafts([]);
    setSurveyorDraftError(null);
    setWorkOrderSaveValidationError(null);
  }, [documentId]);

  const addSurveyorDraft = useCallback(
    (sampleInformationPointDocumentId: string, user: StrapiManagedUserRow) => {
      setSurveyorDraftError(null);
      const pt = samples
        .flatMap((s) => s.sample_information_points ?? [])
        .find((p) => p.documentId === sampleInformationPointDocumentId);
      const existsOnServer =
        pt?.detail_surveyors?.some((sv) => surveyorAlreadyOnSample(sv.user, user)) ?? false;
      if (existsOnServer) {
        setSurveyorDraftError('User ini sudah ada sebagai surveyor untuk titik sampel tersebut.');
        return;
      }
      setPendingSurveyorDrafts((prev) => {
        const existsPending = prev.some(
          (p) =>
            p.sampleInformationPointDocumentId === sampleInformationPointDocumentId &&
            (p.user.id === user.id || (Boolean(p.user.email) && p.user.email === user.email)),
        );
        if (existsPending) {
          setSurveyorDraftError('User ini sudah ada di daftar (belum disimpan).');
          return prev;
        }
        return [...prev, { draftKey: newDraftSurveyorKey(), sampleInformationPointDocumentId, user }];
      });
    },
    [samples],
  );

  const removeSurveyorDraft = useCallback((draftKey: string) => {
    setPendingSurveyorDrafts((prev) => prev.filter((p) => p.draftKey !== draftKey));
  }, []);

  function updateSurveyTeamDraft(draftKey: string, patch: Partial<SurveyTeamDraft>) {
    setSurveyTeamDrafts((prev) => prev.map((row) => (row.draftKey === draftKey ? { ...row, ...patch } : row)));
    setWorkOrderSaveValidationError(null);
  }

  function removeSurveyTeamDraft(draftKey: string) {
    const removed = surveyTeamDrafts.find((row) => row.draftKey === draftKey);
    const removedUserId = removed?.user?.id;
    setSurveyTeamDrafts((prev) => prev.filter((row) => row.draftKey !== draftKey));
    if (removedUserId) {
      const purgeUserCells = (cells: Set<string>) => {
        const next = new Set(cells);
        for (const key of cells) {
          if (key.startsWith(`${removedUserId}:`)) next.delete(key);
        }
        return next;
      };
      setSavedScheduleCells(purgeUserCells);
      setDraftScheduleCells(purgeUserCells);
    }
    setWorkOrderSaveValidationError(null);
  }

  function openSurveyScheduleDialog() {
    setDraftScheduleCells(new Set(savedScheduleCells));
    setSurveyScheduleSaveError(null);
    setSurveyScheduleDialogOpen(true);
  }

  function closeSurveyScheduleDialog() {
    setSurveyScheduleDialogOpen(false);
    setSurveyScheduleSaveError(null);
  }

  function isScheduleCellBlocked(user: StrapiManagedUserRow, date: string): boolean {
    return (surveySchedulesQ.data ?? []).some((row) => {
      const rowUser = dynamicRelation(row, 'user_idx');
      const rowUserId = Number(rowUser?.id);
      const rowWo = dynamicRelation(row, 'work_order');
      return (
        user.numericId != null &&
        Number.isFinite(rowUserId) &&
        rowUserId === user.numericId &&
        surveyTeamRowScheduledOnDate(row, date) &&
        rowWo?.documentId !== documentId
      );
    });
  }

  function handleSaveSurveySchedule() {
    setSurveyScheduleSaveError(null);
    for (const cellKey of draftScheduleCells) {
      const separatorIdx = cellKey.indexOf(':');
      if (separatorIdx <= 0) continue;
      const userId = cellKey.slice(0, separatorIdx);
      const date = cellKey.slice(separatorIdx + 1);
      const user = surveyTeamUsers.find((row) => row.id === userId);
      if (!user) {
        setSurveyScheduleSaveError('Schedule memuat user yang tidak ada di tim survey.');
        return;
      }
      if (isScheduleCellBlocked(user, date)) {
        setSurveyScheduleSaveError(
          `Slot ${date} untuk ${userDisplayName(user)} sudah terisi work order lain. Hapus pilihan tersebut sebelum menyimpan.`,
        );
        return;
      }
    }
    setSavedScheduleCells(new Set(draftScheduleCells));
    closeSurveyScheduleDialog();
  }

  function goHash(nextTab: WorkOrderRouteTab, nextSampleId?: string | null) {
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: buildWorkOrderRouteHash(nextTab, nextSampleId),
      },
      { replace: true },
    );
  }

  async function handleSaveWorkOrder() {
    setWorkOrderSaveValidationError(null);

    if (!jwt) {
      setWorkOrderSaveValidationError('Sesi tidak valid atau Anda belum masuk. Silakan login kembali.');
      return;
    }
    if (!documentId) {
      setWorkOrderSaveValidationError('ID work order tidak valid.');
      return;
    }
    if (!workOrderDate?.trim()) {
      setWorkOrderSaveValidationError('Isi tanggal work order di tab Detail & jadwal.');
      return;
    }
    const yearInt = Math.trunc(Number(calendarYear));
    if (!Number.isFinite(yearInt) || yearInt < 1900 || yearInt > 2100) {
      setWorkOrderSaveValidationError('Isi tahun kalender yang valid (1900–2100) di tab Detail & jadwal.');
      return;
    }

    const drafts = [...pendingSurveyorDrafts];
    const surveyorChainError = validatePendingSurveyorDraftsForWorkOrderSave(samples, drafts);
    if (surveyorChainError) {
      setWorkOrderSaveValidationError(surveyorChainError);
      return;
    }

    const surveyTeamRows = surveyTeamDrafts.filter((row) => row.user != null);
    const surveyTeamUserIds = new Set<string>();
    for (const row of surveyTeamRows) {
      const user = row.user!;
      const key = user.documentId ?? (user.numericId != null ? `n:${user.numericId}` : user.id);
      if (surveyTeamUserIds.has(key)) {
        setWorkOrderSaveValidationError('Tim Survey memiliki user duplikat. Hapus salah satu sebelum menyimpan.');
        return;
      }
      surveyTeamUserIds.add(key);
      try {
        assigneeRelationPayload(user);
      } catch {
        setWorkOrderSaveValidationError('Salah satu user Tim Survey tidak punya id Strapi yang valid.');
        return;
      }
    }

    if (hasAirSamples) {
      const airError = samplingPlanAirRef.current?.validate() ?? null;
      if (airError) {
        setWorkOrderSaveValidationError(airError);
        goHash('sampling_plan_air');
        return;
      }
    }
    if (hasNonAirSamples) {
      const nonAirError = samplingPlanNonAirRef.current?.validate() ?? null;
      if (nonAirError) {
        setWorkOrderSaveValidationError(nonAirError);
        goHash('sampling_plan_non_air');
        return;
      }
    }

    updateMut.reset();
    createSurveyorMut.reset();
    setSaveAllInProgress(true);
    try {
      if (hasAirSamples) {
        await samplingPlanAirRef.current?.save();
      }
      if (hasNonAirSamples) {
        await samplingPlanNonAirRef.current?.save();
      }

      const plans = await strapiListSamplingPlansForWorkOrder(jwt, documentId);
      const usesGroups = quotationUsesSampleGroups(wo?.quotation ?? {});
      const allEquipmentChecklistComplete = allVisibleSamplesHaveEquipmentChecklist(
        samples,
        plans,
        usesGroups,
      );
      const statusToSave = workOrderStatusAfterEquipmentChecklistComplete(status, allEquipmentChecklistComplete);

      await updateMut.mutateAsync({
        documentId,
        data: {
          work_order_date: workOrderDate,
          progress_status: statusToSave,
          roman_quarter: romanQuarter,
          calendar_year: yearInt,
        },
      });
      if (statusToSave !== status) {
        setStatus(statusToSave);
      }
      // Rantai data: quotation sample line → titik (`sample_information_point`) → surveyor.
      if (drafts.length > 0) {
        for (const d of drafts) {
          await createSurveyorMut.mutateAsync({
            sampleInformationPointDocumentId: d.sampleInformationPointDocumentId,
            user: d.user,
          });
        }
      }
      const keptTeamIds = new Set(
        surveyTeamDrafts.map((row) => row.documentId).filter((id): id is string => Boolean(id)),
      );
      for (const existing of surveyTeamsQ.data ?? []) {
        const existingId = String(existing.documentId ?? '').trim();
        if (!existingId) continue;
        if (!keptTeamIds.has(existingId)) {
          await strapiDeleteSurveyTeam(jwt, existingId);
          continue;
        }
        const row = surveyTeamRows.find((draft) => draft.documentId === existingId);
        if (!row?.user) continue;
        await strapiUpdateSurveyTeam(jwt, existingId, {
          is_lead: row.is_lead,
          scheduled_date: scheduleDatesForUser(savedScheduleCells, row.user.id),
        });
      }
      setSurveyTeamsReady(false);
      await surveyTeamsQ.refetch();
      setPendingSurveyorDrafts([]);
      /** Pastikan WO detail di cache telah berisi surveyor baru (populate) sebelum pindah ke view. */
      await qc.fetchQuery({
        queryKey: queryKeys.workOrderDetail(jwt, documentId),
        queryFn: () => strapiGetWorkOrder(jwt, documentId),
      });
      navigate(`/sampling/work-order/${encodeURIComponent(documentId)}/view`, {
        state:
          statusToSave !== status
            ? {
                statusAdvanceMessage: `Checklist alat sudah lengkap untuk semua jenis pengukuran. Status work order diperbarui menjadi «${workOrderStatusLabel[statusToSave]}».`,
              }
            : undefined,
      });
    } catch (err) {
      setWorkOrderSaveValidationError(
        err instanceof Error ? err.message : 'Gagal menyimpan work order atau checklist.',
      );
    } finally {
      setSaveAllInProgress(false);
    }
  }

  if (!documentId) {
    return (
      <PageShell title="Ubah work order">
        <Alert severity="warning">ID work order tidak valid.</Alert>
        <Button component={RouterLink} to="/sampling/work-orders" sx={{ mt: 2 }}>
          Kembali
        </Button>
      </PageShell>
    );
  }

  if (authLoading || !allowEditWorkOrder) {
    return (
      <PageShell fill title="Ubah work order">
        <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            {authLoading ? 'Memuat sesi…' : 'Mengalihkan ke detail work order…'}
          </Typography>
        </Box>
      </PageShell>
    );
  }

  if (detailQ.isPending || !fieldsReady) {
    return (
      <PageShell fill title="Ubah work order">
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      </PageShell>
    );
  }

  if (detailQ.isError || !wo) {
    return (
      <PageShell title="Ubah work order">
        <Alert severity="error" sx={{ mb: 2 }}>
          {detailQ.error instanceof Error ? detailQ.error.message : 'Gagal memuat work order.'}
        </Alert>
        <Button component={RouterLink} to="/sampling/work-orders">
          Kembali
        </Button>
      </PageShell>
    );
  }

  const editActiveTab: WorkOrderRouteTab =
    resolvedSamplingPlanTab ??
    (activeTab === 'survey_team' ? 'survey_team' : 'detail');

  return (
    <PageShell
      fill
      title="Ubah work order"
      extras={
        <Tabs
          value={editActiveTab}
          onChange={(_, value: WorkOrderRouteTab) => {
            if (value === 'detail') {
              goHash('detail');
            } else if (value === 'survey_team') {
              goHash('survey_team');
            } else {
              goHash(value, null);
            }
          }}
          sx={PAGE_SHELL_SCROLLABLE_TABS_SX}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="Bagian ubah work order"
        >
          <Tab label="Detail & jadwal" value="detail" data-testid="work-order-edit-tab-schedule" />
          <Tab
            label="Daftar Sample"
            value="sampling_plan"
            data-testid="work-order-edit-tab-sampling-plan"
          />
          {hasAirSamples ? (
            <Tab
              label={samplingPlanTabLabel('air')}
              value="sampling_plan_air"
              data-testid="work-order-edit-tab-sampling-plan-air"
            />
          ) : null}
          {hasNonAirSamples ? (
            <Tab
              label={samplingPlanTabLabel('non_air')}
              value="sampling_plan_non_air"
              data-testid="work-order-edit-tab-sampling-plan-non-air"
            />
          ) : null}
          <Tab label="Tim Survey" value="survey_team" data-testid="work-order-edit-tab-survey-team" />
        </Tabs>
      }
    >
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <CollapsibleKeteranganSplit
          toggleTestId="work-order-edit-info"
          keterangan={
            <Stack spacing={1}>
              <WorkOrderDetailIntroBlurb wo={wo} />
              {surveyorDraftError ? (
                <Alert
                  severity="error"
                  onClose={() => setSurveyorDraftError(null)}
                  data-testid="work-order-surveyor-draft-error"
                >
                  {surveyorDraftError}
                </Alert>
              ) : null}
              {workOrderSaveValidationError ? (
                <Alert
                  severity="error"
                  onClose={() => setWorkOrderSaveValidationError(null)}
                  data-testid="work-order-edit-validation-error"
                >
                  {workOrderSaveValidationError}
                </Alert>
              ) : null}
              {updateMut.isError ? (
                <Alert severity="error">
                  {updateMut.error instanceof Error
                    ? updateMut.error.message
                    : 'Gagal menyimpan work order.'}
                </Alert>
              ) : null}
              {createSurveyorMut.isError ? (
                <Alert severity="error">
                  {createSurveyorMut.error instanceof Error
                    ? createSurveyorMut.error.message
                    : 'Gagal menyimpan satu atau lebih surveyor.'}
                </Alert>
              ) : null}
              {pendingSurveyorDrafts.length > 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="work-order-edit-pending-surveyors-hint"
                >
                  {pendingSurveyorDrafts.length} surveyor baru akan disimpan ke server saat Anda
                  menekan Simpan work order.
                </Typography>
              ) : null}
            </Stack>
          }
        >
        {editActiveTab === 'detail' && (
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            <TextField
              label="Job number (work order)"
              value={wo.job_number?.trim() ? wo.job_number : '—'}
              fullWidth
              size="small"
              slotProps={{ input: { readOnly: true } }}
              sx={{ '& .MuiInputBase-input': { cursor: 'default' } }}
              helperText="Nomor GL diisi otomatis saat work order dibuat."
            />
            <TextField
              label="Tanggal work order"
              type="date"
              value={workOrderDate}
              onChange={(ev) => {
                setWorkOrderDate(ev.target.value);
                setWorkOrderSaveValidationError(null);
              }}
              required
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <WorkOrderStatusTooltip status={status}>
              <TextField
                select
                label="Status"
                value={status}
                onChange={(ev) => {
                  setStatus(ev.target.value as WorkOrderStatus);
                  setWorkOrderSaveValidationError(null);
                }}
                required
                fullWidth
                size="small"
              >
                {statuses.map((s) => (
                  <MenuItem key={s} value={s}>
                    <WorkOrderStatusMenuItemLabel status={s} />
                  </MenuItem>
                ))}
              </TextField>
            </WorkOrderStatusTooltip>
            <TextField
              select
              label="Bulan (angka Romawi)"
              value={romanQuarter}
              onChange={(ev) => {
                setRomanQuarter(ev.target.value as WorkOrderRomanQuarter);
                setWorkOrderSaveValidationError(null);
              }}
              required
              fullWidth
              size="small"
              helperText="I = Januari … XII = Desember."
            >
              {romanOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Tahun kalender"
              type="number"
              value={calendarYear}
              onChange={(ev) => {
                setCalendarYear(Number.parseInt(ev.target.value, 10) || defaultYear);
                setWorkOrderSaveValidationError(null);
              }}
              required
              fullWidth
              size="small"
              inputProps={{ min: 1900, max: 2100 }}
              helperText={`Sesuaikan jika berbeda dari tahun berjalan (${defaultYear}).`}
            />
            <TextField
              label="Mulai pengujian"
              type="datetime-local"
              value={testingStart}
              fullWidth
              size="small"
              disabled
              InputLabelProps={{ shrink: true }}
              helperText={WORK_ORDER_TESTING_DATES_HELPER}
            />
            <TextField
              label="Selesai pengujian"
              type="datetime-local"
              value={testingEnd}
              fullWidth
              size="small"
              disabled
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        )}

        {editActiveTab === 'sampling_plan' ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Daftar jenis pengukuran pada work order. Isi checklist peralatan di tab air / udara sesuai grouping.
            Semua perubahan (termasuk checklist) disimpan lewat tombol Simpan work order. Jika checklist alat
            lengkap, status otomatis naik dari Draft ke <strong>Terbuka</strong>.
          </Alert>
        ) : null}

        {editActiveTab === 'sampling_plan' && (
          <WorkOrderSamplingPlanTab
            samples={samples}
            workOrderDocumentId={documentId}
            quotationUsesGroups={quotationUsesSampleGroups(wo.quotation ?? {})}
            layout="table"
          />
        )}
        {hasAirSamples ? (
          <Box
            sx={{ display: editActiveTab === 'sampling_plan_air' ? 'block' : 'none' }}
            data-testid="work-order-edit-sampling-plan-air-panel"
          >
            <WorkOrderSamplingPlanTab
              ref={samplingPlanAirRef}
              samples={samples}
              workOrderDocumentId={documentId}
              quotationUsesGroups={quotationUsesSampleGroups(wo.quotation ?? {})}
              airGroup="air"
              layout="inline_form"
              showSaveButton={false}
            />
          </Box>
        ) : null}
        {hasNonAirSamples ? (
          <Box
            sx={{ display: editActiveTab === 'sampling_plan_non_air' ? 'block' : 'none' }}
            data-testid="work-order-edit-sampling-plan-non-air-panel"
          >
            <WorkOrderSamplingPlanTab
              ref={samplingPlanNonAirRef}
              samples={samples}
              workOrderDocumentId={documentId}
              quotationUsesGroups={quotationUsesSampleGroups(wo.quotation ?? {})}
              airGroup="non_air"
              layout="inline_form"
              showSaveButton={false}
            />
          </Box>
        ) : null}

        {editActiveTab === 'survey_team' && (
          <Stack spacing={1.5} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Anggota tim survey ditambahkan oleh admin sampling. Di halaman ini anggota yang sudah ada bisa
                  diatur lead dan jadwalnya, lalu disimpan bersama work order.
                </Typography>
                {savedScheduleCells.size > 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {savedScheduleCells.size} slot schedule tersimpan dari modal jadwal.
                  </Typography>
                ) : null}
              </Stack>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={openSurveyScheduleDialog}
              >
                Lihat schedule semua tim
              </Button>
            </Box>
            {surveyTeamsQ.isError ? (
              <Alert severity="error">
                {surveyTeamsQ.error instanceof Error ? surveyTeamsQ.error.message : 'Gagal memuat tim survey.'}
              </Alert>
            ) : null}
            <Stack spacing={1}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 900, '& .MuiTableCell-root': { verticalAlign: 'middle' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 56 }}>#</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell sx={{ width: 140 }}>Lead</TableCell>
                      <TableCell align="right" sx={{ width: 88 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {surveyTeamDrafts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            Belum ada anggota tim survey.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      surveyTeamDrafts.map((row, idx) => (
                        <TableRow key={row.draftKey}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            <Autocomplete
                              options={picAutocompleteOptions}
                              value={row.user}
                              onChange={(_, v) => updateSurveyTeamDraft(row.draftKey, { user: v })}
                              loading={usersQ.isPending}
                              disabled
                              getOptionLabel={(u) => (u.username?.trim() ? u.username : u.email) || u.id}
                              isOptionEqualToValue={(a, b) => a.id === b.id}
                              renderInput={(params) => <TextField {...params} label="User" size="small" />}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={row.is_lead}
                                  onChange={(ev) => updateSurveyTeamDraft(row.draftKey, { is_lead: ev.target.checked })}
                                />
                              }
                              label="Lead"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              aria-label="Hapus anggota tim survey"
                              onClick={() => removeSurveyTeamDraft(row.draftKey)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
            <Dialog
              open={surveyScheduleDialogOpen}
              onClose={closeSurveyScheduleDialog}
              fullWidth
              maxWidth="xl"
              PaperProps={{ sx: { width: 'min(1280px, 96vw)' } }}
            >
              <DialogTitle>Schedule semua tim survey</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Kolom adalah tanggal dalam minggu aktif, baris adalah anggota tim survey. Klik slot kosong
                    untuk memilih; satu anggota bisa dipilih di lebih dari satu hari.
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          bgcolor: 'grey.300',
                          border: 1,
                          borderColor: 'grey.500',
                          borderRadius: 0.5,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Slot kosong
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{ width: 18, height: 18, bgcolor: 'error.main', borderRadius: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        Work order lain
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{ width: 18, height: 18, bgcolor: 'primary.main', borderRadius: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        Work order ini
                      </Typography>
                    </Stack>
                  </Stack>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <IconButton
                      type="button"
                      aria-label="Minggu sebelumnya"
                      onClick={() => setSurveyScheduleWeekStart(addWeeks(activeWeekStart, -1))}
                      sx={{ p: 1.25 }}
                    >
                      <ArrowBackIosNewIcon sx={{ fontSize: 28 }} />
                    </IconButton>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: 'center', flex: 1 }}>
                      {scheduleWeekRangeDateLabel(surveyScheduleWeekDates[0])} -{' '}
                      {scheduleWeekRangeDateLabel(surveyScheduleWeekDates[6])}
                    </Typography>
                    <IconButton
                      type="button"
                      aria-label="Minggu berikutnya"
                      onClick={() => setSurveyScheduleWeekStart(addWeeks(activeWeekStart, 1))}
                      sx={{ p: 1.25 }}
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: 28 }} />
                    </IconButton>
                  </Box>
                  {surveyScheduleSaveError ? <Alert severity="error">{surveyScheduleSaveError}</Alert> : null}
                  {surveySchedulesQ.isPending && surveyScheduleUserIds.length > 0 ? (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : surveySchedulesQ.isError ? (
                    <Alert severity="error">
                      {surveySchedulesQ.error instanceof Error
                        ? surveySchedulesQ.error.message
                        : 'Gagal memuat schedule user.'}
                    </Alert>
                  ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table
                        size="small"
                        sx={{
                          width: '100%',
                          tableLayout: 'fixed',
                          minWidth: Math.max(720, 280 + surveyScheduleWeekDates.length * 68),
                          '& .MuiTableCell-root': { verticalAlign: 'middle' },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 280 }}>Tim survey</TableCell>
                            <TableCell sx={{ width: 120 }} align="center">
                              Availability
                            </TableCell>
                            {surveyScheduleWeekDates.map((date) => (
                              <TableCell key={date} align="center" sx={{ width: 68, px: 0.5 }}>
                                <Tooltip title={scheduleDayTitle(date)}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                    {scheduleDayHeaderLabel(date)}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {surveyScheduleWeekDates.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={surveyScheduleWeekDates.length + 2}>
                                <Typography variant="body2" color="text.secondary">
                                  Tanggal survey belum tersedia. Isi tanggal work order atau mulai pengujian.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : surveyTeamDrafts.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={surveyScheduleWeekDates.length + 2}>
                                <Typography variant="body2" color="text.secondary">
                                  Belum ada anggota tim survey.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            surveyTeamDrafts.map((draft) => {
                              const user = draft.user;
                              const weekBlocks =
                                user != null
                                  ? surveyScheduleWeekDates.filter((date) =>
                                      (surveySchedulesQ.data ?? []).some((row) => {
                                        const rowUser = dynamicRelation(row, 'user_idx');
                                        const rowUserId = Number(rowUser?.id);
                                        const rowWo = dynamicRelation(row, 'work_order');
                                        return (
                                          user.numericId != null &&
                                          Number.isFinite(rowUserId) &&
                                          rowUserId === user.numericId &&
                                          surveyTeamRowScheduledOnDate(row, date) &&
                                          rowWo?.documentId !== documentId
                                        );
                                      }),
                                    ).length
                                  : 0;
                              const availableDays = surveyScheduleWeekDates.length - weekBlocks;
                              return (
                                <TableRow key={draft.draftKey}>
                                  <TableCell sx={{ p: 1 }}>
                                    <Autocomplete
                                      options={picAutocompleteOptions}
                                      value={draft.user}
                                      onChange={(_, v) => updateSurveyTeamDraft(draft.draftKey, { user: v })}
                                      loading={usersQ.isPending}
                                      disabled
                                      getOptionLabel={(u) => (u.username?.trim() ? u.username : u.email) || u.id}
                                      isOptionEqualToValue={(a, b) => a.id === b.id}
                                      size="small"
                                      sx={{ flex: 1, minWidth: 0 }}
                                      renderInput={(params) => (
                                        <TextField {...params} placeholder="User" size="small" />
                                      )}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    {user ? (
                                      <>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                          {availableDays}/{surveyScheduleWeekDates.length}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          available
                                        </Typography>
                                      </>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        —
                                      </Typography>
                                    )}
                                  </TableCell>
                                  {surveyScheduleWeekDates.map((date) => {
                                  if (!user) {
                                    return (
                                      <TableCell key={`${draft.draftKey}:${date}`} align="center" sx={{ p: 0.75 }}>
                                        <Box
                                          sx={{
                                            width: '100%',
                                            height: 48,
                                            borderRadius: 1,
                                            bgcolor: 'grey.200',
                                            border: '1px solid',
                                            borderColor: 'grey.400',
                                          }}
                                        />
                                      </TableCell>
                                    );
                                  }
                                  const schedules = (surveySchedulesQ.data ?? []).filter((row) => {
                                    const rowUser = dynamicRelation(row, 'user_idx');
                                    const rowUserId = Number(rowUser?.id);
                                    return (
                                      user.numericId != null &&
                                      Number.isFinite(rowUserId) &&
                                      rowUserId === user.numericId &&
                                      surveyTeamRowScheduledOnDate(row, date)
                                    );
                                  });
                                  const blockingSchedule = schedules.find(
                                    (row) => dynamicRelation(row, 'work_order')?.documentId !== documentId,
                                  );
                                  const currentSchedule = schedules.find(
                                    (row) => dynamicRelation(row, 'work_order')?.documentId === documentId,
                                  );
                                  const cellKey = `${user.id}:${date}`;
                                  const selected = draftScheduleCells.has(cellKey);
                                  const rowWo = blockingSchedule ? dynamicRelation(blockingSchedule, 'work_order') : null;
                                  const isBlocked = Boolean(blockingSchedule);
                                  const isCurrentWo = Boolean(currentSchedule) || selected;
                                  const tooltipTitle = isBlocked
                                    ? `Terisi work order lain: ${String(rowWo?.job_number ?? 'WO lain')}`
                                    : isCurrentWo
                                      ? 'Work order ini'
                                      : 'Slot kosong — klik untuk pilih';

                                  return (
                                    <TableCell key={cellKey} align="center" sx={{ p: 0.75 }}>
                                      <Tooltip title={tooltipTitle}>
                                        <Box
                                          component="button"
                                          type="button"
                                          disabled={isBlocked}
                                          aria-label={tooltipTitle}
                                          onClick={() => {
                                            if (isBlocked) return;
                                            setDraftScheduleCells((prev) => {
                                              const next = new Set(prev);
                                              if (next.has(cellKey)) next.delete(cellKey);
                                              else next.add(cellKey);
                                              return next;
                                            });
                                          }}
                                          sx={{
                                            width: '100%',
                                            height: 48,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: isBlocked
                                              ? 'error.dark'
                                              : isCurrentWo
                                                ? 'primary.dark'
                                                : 'grey.500',
                                            bgcolor: isBlocked
                                              ? 'error.main'
                                              : isCurrentWo
                                                ? 'primary.main'
                                                : 'grey.300',
                                            cursor: isBlocked ? 'not-allowed' : 'pointer',
                                            p: 0,
                                            display: 'block',
                                            transition: 'opacity 0.15s ease',
                                            '&:hover': isBlocked
                                              ? {}
                                              : {
                                                  opacity: 0.85,
                                                },
                                            '&:disabled': {
                                              opacity: 1,
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'flex-end', px: 3, py: 2 }}>
                <Stack direction="row" spacing={1}>
                  <Button type="button" onClick={closeSurveyScheduleDialog}>
                    Tutup
                  </Button>
                  <Button type="button" variant="contained" onClick={handleSaveSurveySchedule}>
                    Simpan
                  </Button>
                </Stack>
              </DialogActions>
            </Dialog>
          </Stack>
        )}
        </CollapsibleKeteranganSplit>

        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            py: 1,
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Button
            type="button"
            variant="contained"
            disabled={saveAllInProgress}
            data-testid="work-order-edit-submit"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void handleSaveWorkOrder();
            }}
          >
            {saveAllInProgress ? 'Menyimpan…' : 'Simpan work order'}
          </Button>
          <Button component={RouterLink} to={`/sampling/work-order/${encodeURIComponent(documentId)}/view`}>
            Batal
          </Button>
          <Button component={RouterLink} to="/sampling/work-orders">
            Ke daftar work order
          </Button>
        </Box>
      </Box>
    </PageShell>
  );
}
